import { useCustomMutation } from '@refinedev/core';
import { Switch, message } from 'antd';
import { useState, type FC } from 'react';

interface PlanGate {
  featureKey: string;
  label: string;
  category: string;
  plans: Record<string, boolean>;
}

interface PlanGatesGridProps {
  grouped: Map<string, PlanGate[]>;
  plans: readonly string[];
  onRefetch: () => void;
}

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  solo: 'Solo',
  starter: 'Starter',
  team: 'Team',
  enterprise: 'Enterprise',
};

const CATEGORY_LABELS: Record<string, string> = {
  Core: 'Core',
  Intelligence: 'Intelligence',
  SAV: 'SAV',
  TestRecipe: 'TestRecipe',
  DevOps: 'DevOps',
  Collaboration: 'Collaboration',
  Integrations: 'Integrations',
  Security: 'Securite',
  Alerts: 'Alertes',
  Agenda: 'Agenda',
};

const PlanGatesGrid: FC<PlanGatesGridProps> = ({ grouped, plans, onRefetch }) => {
  const { mutate } = useCustomMutation();
  const [toggling, setToggling] = useState<string | null>(null);

  const handleToggle = (featureKey: string, plan: string, enabled: boolean) => {
    const key = `${featureKey}:${plan}`;
    setToggling(key);

    mutate(
      {
        url: `/api/v1/admin/plan-gates/${featureKey}`,
        method: 'put',
        values: { plan, enabled },
      },
      {
        onSuccess: () => {
          void message.success(`Feature "${featureKey}" ${enabled ? 'activee' : 'desactivee'} pour ${plan}`);
          setToggling(null);
          onRefetch();
        },
        onError: () => {
          void message.error('Erreur lors de la modification');
          setToggling(null);
        },
      },
    );
  };

  const categories = Array.from(grouped.entries());

  return (
    <div className="space-y-6">
      {categories.map(([category, gates]) => (
        <div key={category} className="fc-card overflow-hidden p-0">
          <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-700 dark:bg-slate-800">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              {CATEGORY_LABELS[category] ?? category}
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700">
                  <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Feature
                  </th>
                  {plans.map((plan) => (
                    <th
                      key={plan}
                      className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400"
                    >
                      {PLAN_LABELS[plan] ?? plan}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gates.map((gate) => (
                  <tr
                    key={gate.featureKey}
                    className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50"
                  >
                    <td className="px-5 py-3">
                      <span className="text-sm text-slate-900 dark:text-white">
                        {gate.label}
                      </span>
                      <span className="ml-2 font-mono text-[10px] text-slate-400 dark:text-slate-500">
                        {gate.featureKey}
                      </span>
                    </td>
                    {plans.map((plan) => {
                      const key = `${gate.featureKey}:${plan}`;
                      const enabled = gate.plans[plan] ?? false;
                      return (
                        <td key={plan} className="px-4 py-3 text-center">
                          <Switch
                            size="small"
                            checked={enabled}
                            loading={toggling === key}
                            onChange={(checked) =>
                              handleToggle(gate.featureKey, plan, checked)
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PlanGatesGrid;
